import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, post, del, apiRequest } from "../../lib/request"
import { sortPostCommentsByTime } from "../../lib/utils/postComments"
import {
  getCommentAttachmentUrl,
  injectEditRequestToMediaUrl,
  parseCommentBodyForEdit,
} from "../../components/common/media-preview/commentPreviewHtml"

export const fetchCommentsByPostIds = createAsyncThunk(
  "comments/fetchByPostIds",
  async (topicIds, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      if (!topicIds || topicIds.length === 0) {
        throw new Error("Topic IDs are required")
      }

      const queryString = topicIds.map((id) => `topicIds=${id}`).join("&")
      const endpoint = `/comments?${queryString}`


      const data = await get(endpoint)


      const commentsByPostId = {}

      if (Array.isArray(data)) {
        data.forEach((comment) => {
          const postId = comment.postId
          if (!commentsByPostId[postId]) {
            commentsByPostId[postId] = []
          }
          commentsByPostId[postId].push(normalizeCommentApiRow(comment, postId))
        })
        Object.keys(commentsByPostId).forEach((postId) => {
          commentsByPostId[postId] = sortPostCommentsByTime(commentsByPostId[postId], "asc")
        })
      }

          
      return { commentsByPostId, totalComments: data.length }
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch comments")
    }
  },
)

export const fetchCommentsByPostId = createAsyncThunk(
  "comments/fetchByPostId",
  async (postId, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      if (!postId) {
        throw new Error("Post ID is required")
      }

      const data = await get(`comments/posts/${postId}`)
      return { postId, comments: data }
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch comments")
    }
  },
)

function getCommentRowId(comment) {
  return comment?.comment?.id ?? comment?.postCommentId ?? comment?.id ?? null
}

function normalizeCommentApiRow(row, postId) {
  if (!row || typeof row !== "object") return row
  const mediaLink = row.mediaLink ?? row.comment?.mediaLink ?? null
  const commentText = row.commentText ?? row.comment?.commentText ?? ""
  const postCommentId = row.postCommentId ?? row.id ?? row.comment?.id ?? null
  return {
    ...row,
    postId: row.postId ?? postId ?? null,
    postCommentId,
    commentText,
    mediaLink,
    fileUrl: mediaLink || row.fileUrl || null,
    userName:
      row.userName ||
      row.user?.fullName ||
      row.user?.username ||
      row.comment?.user?.fullName ||
      row.comment?.user?.username,
    userId: row.userId ?? row.user?.id ?? row.comment?.user?.id ?? null,
    commentTime: row.commentTime ?? null,
    updatedTime: row.updatedTime ?? null,
  }
}

function mergeUpdatedCommentRow(existing, apiRow, commentText) {
  if (!existing) return apiRow || existing

  const attachmentFromApi = getCommentAttachmentUrl(apiRow)
  let nextText =
    apiRow?.commentText ??
    apiRow?.comment?.commentText ??
    commentText ??
    existing.commentText ??
    existing?.comment?.commentText

  if (
    commentText &&
    commentText.includes("edit-request-reason") &&
    /To\s*<\/strong>\s*https?:\/\//i.test(commentText) &&
    nextText?.includes("edit-request-reason") &&
    !/To\s*<\/strong>\s*https?:\/\//i.test(nextText)
  ) {
    nextText = commentText
  }

  if (attachmentFromApi && nextText?.includes("edit-request-reason")) {
    nextText = injectEditRequestToMediaUrl(nextText, attachmentFromApi)
  } else if (
    nextText?.includes("edit-request-reason") &&
    !/To\s*<\/strong>\s*https?:\/\//i.test(nextText)
  ) {
    const parsed = parseCommentBodyForEdit(nextText)
    const fallbackUrl = parsed.imageUrls?.[0]
    if (fallbackUrl) {
      nextText = injectEditRequestToMediaUrl(nextText, fallbackUrl)
    }
  }

  const mediaLink =
    apiRow?.mediaLink !== undefined
      ? apiRow.mediaLink
      : attachmentFromApi || existing.mediaLink || existing.fileUrl || null

  const merged = {
    ...existing,
    ...(apiRow && typeof apiRow === "object" ? apiRow : {}),
    commentText: nextText,
    postCommentId: apiRow?.id ?? apiRow?.postCommentId ?? existing.postCommentId,
    mediaLink,
    fileUrl: mediaLink || attachmentFromApi || null,
    updatedTime: apiRow?.updatedTime ?? null,
  }

  if (existing.comment && typeof existing.comment === "object") {
    return {
      ...merged,
      comment: {
        ...existing.comment,
        ...(apiRow?.comment && typeof apiRow.comment === "object" ? apiRow.comment : {}),
        commentText: nextText,
      },
    }
  }
  return merged
}

export const updateComment = createAsyncThunk(
  "comments/update",
  async (
    { commentId, postId, comment, file = null, mediaLink = null, removeMedia = false },
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState()
      const token = state.auth?.token
      if (!token) throw new Error("Authentication required. Please login first.")
      if (!commentId) throw new Error("Comment ID is required")
      const commentParam = String(comment ?? "").trim()
      if (!commentParam && !file && !removeMedia) {
        throw new Error("Comment text or an attachment is required")
      }

      let endpoint = `comments/${commentId}?comment=${encodeURIComponent(commentParam)}`
      const mediaLinkParam = mediaLink != null && String(mediaLink).trim() !== "" ? String(mediaLink).trim() : null
      if (mediaLinkParam && !file && !removeMedia) {
        endpoint += `&media_link=${encodeURIComponent(mediaLinkParam)}`
      }

      const formData = new FormData()
      formData.append("file", file || new Blob())
      const data = await apiRequest(endpoint, { method: "PATCH", body: formData })
      return { commentId, postId, commentText: commentParam, data: normalizeCommentApiRow(data, postId) }
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update comment")
    }
  },
)

export const deleteComment = createAsyncThunk(
  "comments/delete",
  async ({ commentId, postId }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token
      if (!token) throw new Error("Authentication required. Please login first.")
      if (!commentId) throw new Error("Comment ID is required")

      await del(`comments/${commentId}`)
      return { commentId, postId }
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete comment")
    }
  },
)

export const createComment = createAsyncThunk(
  "comments/create",
  async ({ postId, comment, parentCommentId = null, file = null }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      if (!postId) {
        throw new Error("Post ID is required")
      }

      if (!comment || comment.trim() === "") {
        if (!file) {
          throw new Error("Comment text or an attachment is required")
        }
      }

      const commentParam = (comment ?? "").trim()
      let endpoint = `comments/posts/${postId}?comment=${encodeURIComponent(commentParam)}`
      if (parentCommentId) {
        endpoint += `&parent_comment_id=${parentCommentId}`
      }

      const formData = new FormData()
      if (file) {
        formData.append("file", file)
      }

      const data = await post(endpoint, formData)

      return { postId, comment: normalizeCommentApiRow(data, postId) }
    } catch (error) {
      console.error(`Failed to create comment for postId ${postId}:`, error.message)
      return rejectWithValue(error.message || "Failed to create comment")
    }
  },
)

const initialState = {
  commentsByPostId: {},
  status: "idle",
  error: null,
  activePostId: null,
  batchStatus: "idle",
  batchError: null,
  failedPostIds: [],
  createStatus: "idle",
  createError: null,
  updateStatus: "idle",
  updateError: null,
  deleteStatus: "idle",
  deleteError: null,
}

const commentsSlice = createSlice({
  name: "comments",
  initialState,
  reducers: {
    clearCommentsError: (state) => {
      state.error = null
      state.batchError = null
      state.createError = null
      state.updateError = null
      state.deleteError = null
    },
    setActivePostId: (state, action) => {
      state.activePostId = action.payload
    },
    clearComments: (state) => {
      state.commentsByPostId = {}
      state.status = "idle"
      state.error = null
      state.activePostId = null
      state.batchStatus = "idle"
      state.batchError = null
      state.failedPostIds = []
      state.createStatus = "idle"
      state.createError = null
      state.updateStatus = "idle"
      state.updateError = null
      state.deleteStatus = "idle"
      state.deleteError = null
    },
    resetCommentsOnLogout: (state) => {
      return initialState
    },
    retryFailedPosts: (state) => {
      state.failedPostIds = []
      state.batchError = null
    },
    retrySpecificPosts: (state, action) => {
      const postIds = action.payload
      state.failedPostIds = state.failedPostIds.filter((id) => !postIds.includes(id))
      state.batchError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommentsByPostId.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchCommentsByPostId.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.error = null
        const { postId, comments } = action.payload
        const normalized = Array.isArray(comments)
          ? comments.map((row) => normalizeCommentApiRow(row, postId))
          : []
        state.commentsByPostId[postId] = sortPostCommentsByTime(normalized, "asc")
      })
      .addCase(fetchCommentsByPostId.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
      .addCase(fetchCommentsByPostIds.pending, (state) => {
        state.batchStatus = "loading"
        state.batchError = null
      })
      .addCase(fetchCommentsByPostIds.fulfilled, (state, action) => {
        state.batchStatus = "succeeded"
        state.batchError = null
        const { commentsByPostId, totalComments } = action.payload

        Object.keys(commentsByPostId).forEach((postId) => {
          state.commentsByPostId[postId] = sortPostCommentsByTime(commentsByPostId[postId], "asc")
        })
      })
      .addCase(fetchCommentsByPostIds.rejected, (state, action) => {
        state.batchStatus = "failed"
        state.batchError = action.payload
      })
      .addCase(createComment.pending, (state) => {
        state.createStatus = "loading"
        state.createError = null
      })
      .addCase(createComment.fulfilled, (state, action) => {
        state.createStatus = "succeeded"
        const { postId, comment } = action.payload
        if (!state.commentsByPostId[postId]) {
          state.commentsByPostId[postId] = []
        }
        state.commentsByPostId[postId].push(comment)
        state.commentsByPostId[postId] = sortPostCommentsByTime(state.commentsByPostId[postId], "asc")
      })
      .addCase(createComment.rejected, (state, action) => {
        state.createStatus = "failed"
        state.createError = action.payload
      })
      .addCase(updateComment.pending, (state) => {
        state.updateStatus = "loading"
        state.updateError = null
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        state.updateStatus = "succeeded"
        const { postId, commentId, commentText, data } = action.payload
        const list = state.commentsByPostId[postId]
        if (!Array.isArray(list)) return
        state.commentsByPostId[postId] = list.map((row) => {
          const rowId = getCommentRowId(row)
          if (String(rowId) !== String(commentId)) return row
          return mergeUpdatedCommentRow(row, data, commentText)
        })
      })
      .addCase(updateComment.rejected, (state, action) => {
        state.updateStatus = "failed"
        state.updateError = action.payload
      })
      .addCase(deleteComment.pending, (state) => {
        state.deleteStatus = "loading"
        state.deleteError = null
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded"
        const { postId, commentId } = action.payload
        const list = state.commentsByPostId[postId]
        if (!Array.isArray(list)) return
        state.commentsByPostId[postId] = list.filter(
          (row) => String(getCommentRowId(row)) !== String(commentId),
        )
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.deleteStatus = "failed"
        state.deleteError = action.payload
      })
  },
})

export const {
  clearCommentsError,
  setActivePostId,
  clearComments,
  resetCommentsOnLogout,
  retryFailedPosts,
  retrySpecificPosts,
} = commentsSlice.actions

export const selectCommentsByPostId = (state, postId) => state.comments.commentsByPostId[postId] || []
export const selectCommentsStatus = (state) => state.comments.status
export const selectCommentsError = (state) => state.comments.error
export const selectActivePostId = (state) => state.comments.activePostId
export const selectBatchStatus = (state) => state.comments.batchStatus
export const selectBatchError = (state) => state.comments.batchError
export const selectFailedPostIds = (state) => state.comments.failedPostIds
export const selectCreateStatus = (state) => state.comments.createStatus
export const selectCreateError = (state) => state.comments.createError

export default commentsSlice.reducer
